import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// 天然裂缝密度剖面: 沿深度的裂缝条数 / 线密度(条/10m), 用于识别裂缝发育段。

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', panelBg: '#F4F6F9', accent: '#C2533A',
};

type Bin = { top: number; bottom: number; count: number; per_10m: number };

export default function FracDensityPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [bins, setBins] = useState<Bin[]>([]);
  const [total, setTotal] = useState(0);
  const [win, setWin] = useState(25);
  const [loading, setLoading] = useState(false);

  const load = async (w = win) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await formationAPI.fractureStats(projectId, { density_window: w });
      setBins(data.density || []);
      setTotal(data.count || 0);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]);

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  const maxD = Math.max(1, ...bins.map((b) => b.per_10m));
  // 发育段判据: 线密度超过全井均值 2 倍
  const mean = bins.length ? bins.reduce((s, b) => s + b.per_10m, 0) / bins.length : 0;
  const hot = (v: number) => mean > 0 && v >= mean * 2;

  return (
    <div style={{ padding: 20, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: TH.text, margin: 0 }}>裂缝密度剖面</h2>
          <div style={{ fontSize: 12, color: TH.textMuted, marginTop: 3 }}>
            共 {total} 条裂缝 · 全井平均 {mean.toFixed(2)} 条/10m
            {mean > 0 && <span> · <span style={{ color: TH.accent }}>红色 = 发育段(≥均值 2 倍)</span></span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: TH.textSec }}>统计窗口(m)</label>
          <select value={win} onChange={(e) => { const w = Number(e.target.value); setWin(w); load(w); }}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${TH.border}` }}>
            {[10, 25, 50, 100].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>

      {!bins.length ? (
        <div style={{ padding: 40, textAlign: 'center', color: TH.textSec, background: TH.panelBg, borderRadius: 6, border: `1px solid ${TH.border}` }}>
          {loading ? '加载中…' : '该项目暂无天然裂缝数据，请先在「裂缝参数」子页导入。'}
        </div>
      ) : (
        <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, padding: '14px 16px' }}>
          <div style={{ display: 'flex', fontSize: 11, color: TH.textMuted, paddingBottom: 6, borderBottom: `1px solid ${TH.border}`, marginBottom: 6 }}>
            <span style={{ width: 130 }}>深度区间 (m)</span>
            <span style={{ width: 54, textAlign: 'right' }}>条数</span>
            <span style={{ width: 78, textAlign: 'right', marginRight: 12 }}>条/10m</span>
            <span>密度</span>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}>
            {bins.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '3px 0' }}>
                <span style={{ width: 130, fontSize: 11, fontFamily: 'monospace', color: TH.textSec }}>
                  {b.top} – {b.bottom}
                </span>
                <span style={{ width: 54, textAlign: 'right', fontSize: 11, fontFamily: 'monospace', color: TH.text }}>
                  {b.count}
                </span>
                <span style={{ width: 78, textAlign: 'right', marginRight: 12, fontSize: 11, fontFamily: 'monospace',
                  color: hot(b.per_10m) ? TH.accent : TH.text, fontWeight: hot(b.per_10m) ? 700 : 400 }}>
                  {b.per_10m.toFixed(2)}
                </span>
                <span style={{ flex: 1, height: 12, background: '#F0F2F5', borderRadius: 2, overflow: 'hidden' }}>
                  <span style={{
                    display: 'block', height: '100%', width: `${(b.per_10m / maxD) * 100}%`,
                    background: hot(b.per_10m) ? TH.accent : '#7FA8C4',
                  }} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
